import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

export interface AuditInfo {
  createdAt?: string | null
  createdByUserId?: string | null
  updatedAt?: string | null
  updatedByUserId?: string | null
}

interface AuditContextValue {
  audit: AuditInfo | null
  setAudit: (a: AuditInfo | null) => void
  /** DOM node in the global Footer (right side) that list pages portal their
   *  pagination into, so "Rows per page" etc. live in the pinned footer. */
  paginationSlot: HTMLElement | null
  setPaginationSlot: (el: HTMLElement | null) => void
}

const AuditContext = createContext<AuditContextValue>({
  audit: null,
  setAudit: () => {},
  paginationSlot: null,
  setPaginationSlot: () => {},
})

/**
 * Holds the "created/modified by" info for the current editor/detail page so the
 * global Footer can render it (right-aligned). Pages push via useSetAuditFooter.
 * Also exposes the Footer's pagination slot for list-page pagination.
 */
export function AuditFooterProvider({ children }: { children: ReactNode }) {
  const [audit, setAudit] = useState<AuditInfo | null>(null)
  const [paginationSlot, setPaginationSlot] = useState<HTMLElement | null>(null)
  return (
    <AuditContext.Provider value={{ audit, setAudit, paginationSlot, setPaginationSlot }}>
      {children}
    </AuditContext.Provider>
  )
}

export function useAuditFooter() {
  return useContext(AuditContext)
}

/**
 * The Footer registers its pagination slot element via `setSlot` (a ref
 * callback); list pages read `slot` and portal their pagination controls into it.
 */
export function useFooterPaginationSlot() {
  const { paginationSlot, setPaginationSlot } = useContext(AuditContext)
  return { slot: paginationSlot, setSlot: setPaginationSlot }
}

/**
 * Register the current page's audit into the footer. Clears on unmount and
 * re-runs when the audit fields change (so it re-arms after a save → refetch).
 *
 * Pass `enabled = false` to make this a complete no-op (no set, no cleanup).
 * Use this when the page owns the footer and passes pushAuditFooter={false}
 * to EntityForm so the form does not clear it on tab switch.
 */
export function useSetAuditFooter(audit: AuditInfo | null | undefined, enabled = true) {
  const { setAudit } = useAuditFooter()
  useEffect(() => {
    if (!enabled) return
    setAudit(audit && (audit.createdAt || audit.updatedAt) ? audit : null)
    return () => setAudit(null)
  }, [
    enabled,
    audit?.createdAt,
    audit?.createdByUserId,
    audit?.updatedAt,
    audit?.updatedByUserId,
    setAudit,
  ])
}
